"use client";
import React, { useState } from "react";
import Image from "next/image";
import type { GetUserImages } from "../actions";
import { If } from "~/components/v3/Conditional";
import { isVideo } from "~/utils/images";
import { RiFileVideoFill } from "react-icons/ri";
import ImageLightbox from "../../events/[eventSlug]/_components/ImageLightbox";
import { env } from "~/env.mjs";
import { Card, CardContent } from "~/components/v4/shadcn/Card";

interface HomeUserImagesProps {
    imagesData: GetUserImages;
}

const HomeUserImages = ({ imagesData }: HomeUserImagesProps) => {
    const hasImages = imagesData && imagesData.length > 0;
    const [activeUrl, setActiveUrl] = useState<string | null>(null);

    if (!hasImages) {
        return null;
    }

    const displayImages = imagesData.slice(0, 4);
    const remainingCount = Math.max(0, imagesData.length - 4);
    const allImageUrls = imagesData.map(
        (image) =>
            `${env.NEXT_PUBLIC_ASSETS_URL}/event/${image.event.id}/${image.name}`,
    );

    // Handle click on +17 overlay to show first remaining image
    const handleOverlayClick = () => {
        if (imagesData.length > 4) {
            const fifthImage = imagesData[4];
            setActiveUrl(
                `${env.NEXT_PUBLIC_ASSETS_URL}/event/${fifthImage?.event.id}/${fifthImage?.name}`,
            );
        }
    };

    return (
        <Card className="flex w-full flex-col gap-4 p-4">
            <span className="text-base text-v3-N-300">Photos</span>

            <CardContent className="p-0">
                <div className="grid grid-cols-2 gap-4 p-1">
                    {displayImages.map((image, index) => {
                        const isLastImage = index === 3;
                        const showOverlay = isLastImage && remainingCount > 0;
                        return (
                            <div
                                key={image.id}
                                className="relative h-max min-h-[120px] w-full"
                            >
                                <If condition={isVideo(image.name)}>
                                    <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 transform flex-col items-center text-center">
                                        <RiFileVideoFill size={42} />
                                        <span className="mt-2 text-sm">
                                            {image.name}
                                        </span>
                                    </div>
                                </If>
                                <If condition={!isVideo(image.name)}>
                                    <Image
                                        className="h-full max-h-[120px] w-full max-w-[120px] cursor-pointer rounded-xl object-cover"
                                        src={`${env.NEXT_PUBLIC_ASSETS_URL}/event/${image.event.id}/${image.name}`}
                                        alt={"event image"}
                                        fill
                                        sizes="(max-width: 512px) 50vw"
                                        onClick={() => {
                                            setActiveUrl(
                                                `${env.NEXT_PUBLIC_ASSETS_URL}/event/${image.event.id}/${image.name}`,
                                            );
                                        }}
                                    />
                                    {showOverlay && (
                                        <div
                                            className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-xl bg-black/50 text-xl font-bold text-white hover:bg-black/70"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOverlayClick();
                                            }}
                                        >
                                            +{remainingCount}
                                        </div>
                                    )}
                                </If>
                            </div>
                        );
                    })}
                    <ImageLightbox urls={allImageUrls} activeUrl={activeUrl} />
                </div>
            </CardContent>
        </Card>
    );
};

export default HomeUserImages;
